import { invokePortalLambda } from './portalLambda';

/**
 * The NextGen account broker. Every call carries the person's portal sign-in
 * token, which the broker verifies (TASK-631).
 */
function invoke<T>(payload: Record<string, unknown>): Promise<T> {
  return invokePortalLambda<T>('NEXTGEN_BROKER_LAMBDA', payload);
}

export interface AccountAvailability {
  accountId: string;
  /** True only when the account is a free NextGen pool account with nothing deployed in it. */
  available: boolean;
  /** Why it cannot be used. Safe to show to the user as written. */
  reason?: string;
  /** What AWS Organizations calls the account today. */
  accountName?: string;
  /** Client stacks already deployed in the account. */
  tenants: string[];
}

export interface NextgenOrgAccount {
  id: string;
  name: string;
  email: string;
  status: string;
}

export const nextgenBrokerService = {
  async updateAccountName(accountId: string, newName: string): Promise<{ accountId: string; name: string }> {
    return invoke({ action: 'updateAccountName', accountId, newName });
  },

  /**
   * The client's system user sign-in. The broker reads the account and region
   * from the client's config, and reads only that client's system user
   * password.
   */
  async getSystemUserSecret(clientName: string): Promise<{ secretName: string; secretString?: string }> {
    return invoke({ action: 'getSystemUserSecret', clientName });
  },

  async checkAccountAvailable(accountId: string, clientId?: string): Promise<AccountAvailability> {
    return invoke({ action: 'checkAccountAvailable', accountId, clientId });
  },

  /** Proves Arcanum can sign in to the account through ArcanumAIAccess. Nothing in the account changes. */
  async precheckAssumeClientRole(accountId: string): Promise<{ assumedRoleArn: string }> {
    return invoke({ action: 'precheckAssumeClientRole', accountId });
  },

  async listOrgAccounts(): Promise<{ accounts: NextgenOrgAccount[] }> {
    return invoke({ action: 'listOrgAccounts' });
  },
};
